import React, { useEffect, useRef, useState } from "react";
import "./BenefitsGrid.scss";

// Icons in: src/resources/images/brandkeeper/
import IcConsistent from "../../../../resources/images/brandkeeper/bk-consistent-visuals.png";
import IcSystem     from "../../../../resources/images/brandkeeper/bk-centralized-system.png";
import IcFlow       from "../../../../resources/images/brandkeeper/bk-creative-flow.png";
import IcZeroStress from "../../../../resources/images/brandkeeper/bk-zero-stress.png";
import IcMonthly    from "../../../../resources/images/brandkeeper/bk-monthly-delivery.png";
import IcFastRight  from "../../../../resources/images/brandkeeper/bk-fast-correct.png";

const BENEFITS = [
  { icon: IcConsistent, title: "Consistent brand visuals",
    copy: "Your brand always shows up polished. No more guessing colors or cobbling fonts. We keep things tight and on point." },
  { icon: IcSystem, title: "Centralized brand system",
    copy: "Everything in one place. No more digging through old files or chasing freelancers for the latest version. We’ve got it handled." },
  { icon: IcFlow, title: "Effortless creative flow",
    copy: "You throw us the idea, we run with it. Quick, clean, and ready to post without the usual drama." },
  { icon: IcZeroStress, title: "Zero creative stress",
    copy: "No need to brief us every time. We know your brand like it’s ours so you can stay in your zone and trust the work will land." },
  { icon: IcMonthly, title: "Reliable monthly delivery",
    copy: "Fresh designs, every month, right when you expect them. You’ll feel like your brand has a rhythm now." },
  { icon: IcFastRight, title: "Get it right faster",
    copy: "No more endless revisions. We get your vibe and hit the mark faster so you can move on and keep things rolling." },
];

export default function BenefitsGrid() {
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const total = BENEFITS.length;

  // measure step size (card width + gap)
  const getStep = () => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return 0;
    const firstCard = track.querySelector(".bg__card");
    if (!firstCard) return 0;

    const cardW = firstCard.getBoundingClientRect().width;
    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || "0");
    return cardW + gap;
  };

  // set index from scrollLeft
  const syncIndexFromScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const step = getStep();
    if (!step) return;
    const newIdx = Math.round(scroller.scrollLeft / step);
    setIdx(Math.max(0, Math.min(total - 1, newIdx)));
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    syncIndexFromScroll();
    scroller.addEventListener("scroll", syncIndexFromScroll, { passive: true });
    const onResize = () => syncIndexFromScroll();
    window.addEventListener("resize", onResize);
    return () => {
      scroller.removeEventListener("scroll", syncIndexFromScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const scrollToIndex = (next) => {
    const scroller = scrollerRef.current;
    const step = getStep();
    if (!scroller || !step) return;
    const clamped = Math.max(0, Math.min(total - 1, next));
    scroller.scrollTo({ left: clamped * step, behavior: "smooth" });
    setIdx(clamped);
  };

  const prevDisabled = idx <= 0;
  const nextDisabled = idx >= total - 1;
  const progressPct = total > 1 ? (idx / (total - 1)) * 100 : 0;

  return (
    <section className="benefits-grid" aria-label="Benefits">
      <div className="bg__inner">
        <p className="bg__kicker">What BrandKeeper™ does</p>

        <h2 className="bg__title">Brand sorted. Business moving.</h2>

        {/* Desktop/Tablet grid */}
        <ul className="bg__grid" role="list">
          {BENEFITS.map((b, i) => (
            <li className="bg__item" key={i}>
              <img className="bg__icon" src={b.icon} alt="" aria-hidden="true" />
              <h3 className="bg__item-title">{b.title}</h3>
              <p className="bg__item-copy">{b.copy}</p>
            </li>
          ))}
        </ul>

        {/* Mobile scroller */}
        <div className="bg__scroller" role="region" aria-label="Benefits (swipe left/right)" ref={scrollerRef}>
          <ul className="bg__track" role="list" ref={trackRef}>
            {BENEFITS.map((b, i) => (
              <li className="bg__card" key={`m-${i}`} aria-roledescription="slide">
                <img className="bg__icon" src={b.icon} alt="" aria-hidden="true" />
                <h3 className="bg__item-title">{b.title}</h3>
                <p className="bg__item-copy">{b.copy}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Mobile controls */}
        <div className="bg__mobile-controls" aria-hidden="true">
          <div className="bg__pill">
            <span className="bg__pill-fill" style={{ width: `${progressPct}%` }} />
          </div>

          <button
            className={`bg__nav bg__nav--prev ${prevDisabled ? "is-disabled" : ""}`}
            onClick={() => scrollToIndex(idx - 1)}
            aria-label="Previous"
            aria-disabled={prevDisabled}
            tabIndex={prevDisabled ? -1 : 0}
          >
            {/* inactive pink arrow */}
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.0023 39.0638C25.4657 39.0638 28.8514 38.0368 31.7311 36.1126C34.6108 34.1885 36.8553 31.4536 38.1807 28.2538C39.5061 25.054 39.8529 21.5331 39.1772 18.1362C38.5015 14.7393 36.8337 11.6191 34.3847 9.17011C31.9357 6.72111 28.8155 5.05332 25.4186 4.37763C22.0218 3.70196 18.5008 4.04874 15.301 5.37413C12.1012 6.69952 9.36635 8.944 7.44218 11.8237C5.51801 14.7034 4.49098 18.0891 4.49098 21.5525C4.49589 26.1953 6.3424 30.6465 9.62535 33.9295C12.9083 37.2124 17.3595 39.0589 22.0023 39.0638ZM22.0023 6.73521C24.9329 6.73521 27.7977 7.60423 30.2344 9.23238C32.6711 10.8605 34.5702 13.1747 35.6917 15.8822C36.8132 18.5897 37.1066 21.5689 36.5349 24.4432C35.9632 27.3175 34.552 29.9577 32.4797 32.0299C30.4075 34.1021 27.7673 35.5134 24.893 36.0851C22.0188 36.6568 19.0395 36.3634 16.332 35.2419C13.6245 34.1204 11.3103 32.2212 9.6822 29.7845C8.05406 27.3479 7.18504 24.4831 7.18504 21.5525C7.18949 17.6241 8.75203 13.8578 11.5298 11.08C14.3077 8.3022 18.0739 6.73967 22.0023 6.73521ZM14.3142 22.5055C14.1889 22.3804 14.0896 22.2319 14.0218 22.0683C13.954 21.9048 13.9191 21.7295 13.9191 21.5525C13.9191 21.3755 13.954 21.2002 14.0218 21.0367C14.0896 20.8731 14.1889 20.7246 14.3142 20.5995L19.7023 15.2114C19.955 14.9586 20.2978 14.8166 20.6553 14.8166C21.0128 14.8166 21.3556 14.9586 21.6083 15.2114C21.8611 15.4641 22.0031 15.8069 22.0031 16.1644C22.0031 16.5218 21.8611 16.8647 21.6083 17.1174L18.5186 20.2055L28.7375 20.2055C29.0947 20.2055 29.4373 20.3474 29.6899 20.6C29.9426 20.8526 30.0845 21.1952 30.0845 21.5525C30.0845 21.9098 29.9426 22.2524 29.6899 22.505C29.4373 22.7576 29.0947 22.8995 28.7375 22.8995L18.5186 22.8995L21.6083 25.9876C21.8611 26.2403 22.0031 26.5832 22.0031 26.9406C22.0031 27.2981 21.8611 27.6409 21.6083 27.8936C21.3556 28.1464 21.0127 28.2884 20.6553 28.2884C20.2978 28.2884 19.955 28.1464 19.7023 27.8936L14.3142 22.5055Z" fill="#FFC0C0"/>
            </svg>
          </button>

          <button
            className={`bg__nav bg__nav--next ${nextDisabled ? "is-disabled" : ""}`}
            onClick={() => scrollToIndex(idx + 1)}
            aria-label="Next"
            aria-disabled={nextDisabled}
            tabIndex={nextDisabled ? -1 : 0}
          >
            {/* active red arrow */}
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.3551 4.04114C17.8917 4.04114 14.506 5.06816 11.6263 6.99233C8.74659 8.9165 6.50212 11.6514 5.17673 14.8512C3.85134 18.051 3.50455 21.5719 4.18023 24.9688C4.85591 28.3656 6.52371 31.4859 8.97271 33.9349C11.4217 36.3839 14.5419 38.0517 17.9388 38.7273C21.3357 39.403 24.8566 39.0562 28.0564 37.7308C31.2562 36.4055 33.9911 34.161 35.9152 31.2813C37.8394 28.4015 38.8664 25.0159 38.8664 21.5525C38.8615 16.9097 37.015 12.4585 33.7321 9.17551C30.4491 5.89255 25.9979 4.04604 21.3551 4.04114ZM21.3551 36.3698C18.4245 36.3698 15.5597 35.5007 13.1231 33.8726C10.6864 32.2445 8.78719 29.9303 7.66571 27.2228C6.54422 24.5153 6.25079 21.536 6.82252 18.6618C7.39425 15.7875 8.80546 13.1473 10.8777 11.0751C12.9499 9.00284 15.5901 7.59163 18.4644 7.0199C21.3387 6.44817 24.3179 6.7416 27.0254 7.86309C29.7329 8.98457 32.0471 10.8837 33.6752 13.3204C35.3034 15.7571 36.1724 18.6219 36.1724 21.5525C36.1679 25.4809 34.6054 29.2471 31.8276 32.025C29.0498 34.8028 25.2835 36.3653 21.3551 36.3698ZM29.0433 20.5995C29.1685 20.7246 29.2678 20.8731 29.3356 21.0366C29.4034 21.2002 29.4383 21.3755 29.4383 21.5525C29.4383 21.7295 29.4034 21.9048 29.3356 22.0683C29.2678 22.2318 29.1685 22.3804 29.0433 22.5055L23.6551 27.8936C23.4024 28.1464 23.0596 28.2884 22.7021 28.2884C22.3447 28.2884 22.0019 28.1464 21.7491 27.8936C21.4963 27.6408 21.3543 27.298 21.3543 26.9406C21.3543 26.5831 21.4963 26.2403 21.7491 25.9876L24.8388 22.8995H14.62C14.2627 22.8995 13.9201 22.7576 13.6675 22.505C13.4149 22.2524 13.2729 21.9097 13.2729 21.5525C13.2729 21.1952 13.4149 20.8526 13.6675 20.6C13.9201 20.3474 14.2627 20.2055 14.62 20.2055H24.8388L21.7491 17.1174C21.4963 16.8646 21.3543 16.5218 21.3543 16.1644C21.3543 15.8069 21.4963 15.4641 21.7491 15.2114C22.0019 14.9586 22.3447 14.8166 22.7021 14.8166C23.0596 14.8166 23.4024 14.9586 23.6551 15.2114L29.0433 20.5995Z" fill="#D70000"/>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}